import * as React from 'react';
import styled from 'styled-components';
import { getColor } from '../../styles/theme';
import { useMemo } from 'react';

type FontAwesomeOptions = {
    style?: 'solid' | 'regular' | 'brands';
};

type IconVariant = 'default' | 'circle' | 'square';

/**
 * Props type for JRCIcon
 * @property name string: Name of the icon (required)
 * @property color, string, optional: icon color, one of the theme color or a hex, rgb color, default to primary
 * @property variant, string, optional: variant of the icon
 * @property size, optional: in px - The icon size, default set to 20px
 * @property isMargin, optional: whether there is a horizontal margin, default to true
 * @property className, optional, className of the <i /> tag
 */
export interface JRCIconProps {
    /** Name of the icon (required) */
    name: string;
    /** Color */
    color?: string;
    /** background color */
    variant?: IconVariant;
    /** Defined the icon's dimensions */
    size?: number;
    /** margin */
    isMargin?: boolean;
    className?: string;
    faOptions?: FontAwesomeOptions;
}

const Icon = styled.i<{
    name: string;
    $color: string;
    $variant: IconVariant;
    $size: number;
    $fontSize: number;
    $isMargin: boolean;
}>`
    flex-shrink: 0;
    text-align: center;
    ${(props) => props.$isMargin && `margin: 0 0.2em`};

    font-size: ${(props) =>
        props.$variant === 'default' ? `${props.$fontSize}px` : `calc(0.6 * ${props.$fontSize}px)`};
    transition: all 0.2s;
    min-width: ${(props) => `${props.$size}px`};
    line-height: ${(props) => `${props.$size}px`};
    ${(props) =>
        ['circle', 'square'].includes(props.$variant) && `background-color: ${getColor(props.theme, props.$color)}`};
    ${(props) => (props.$variant === 'circle' ? `border-radius: 50%` : `border-radius: 20%`)};

    &:before {
        transition: color 0.1s;
        color: ${(props) => (props.$variant === 'default' ? getColor(props.theme, props.$color) : 'white')};
    }
`;

export const JRCIcon = ({
    color = 'primary',
    variant = 'default',
    name,
    size = 20,
    isMargin = true,
    className,
    faOptions,
}: JRCIconProps) => {
    const { faClasses, fontSpot } = useMemo(() => {
        const classes = ['fa', name.replace('icon-', 'fa-')];
        if (faOptions?.style) classes.push(`fa-${faOptions.style}`);
        // Enhance with more options ?
        // if (...) classes.push(`fa-${...}`);
        if (!faOptions) classes.push('react-icon');
        return { faClasses: classes.join(' '), fontSpot: name.indexOf('icon-fs-') === 0 };
    }, [faOptions, name]);

    return (
        <Icon
            name={name}
            $color={color}
            $variant={variant}
            $size={size}
            $fontSize={fontSpot ? size : 0.75 * size}
            $isMargin={isMargin}
            aria-hidden={true}
            className={`${faClasses} ${name} ${className || ''}`}
        />
    );
};
